import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter as Router } from "react-router-dom";
import "@/index.css";
import App from "@/App.tsx";

// Detect if device is iPhone
const isIPhone = /iphone|ipod/i.test(navigator.userAgent);

// Set data-bg attribute EARLY based on URL param (before React renders)
// This ensures CSS rules apply immediately
const urlParams = new URLSearchParams(window.location.search);
const bgParam = urlParams.get('bg');
if (bgParam === 'disabled') {
  document.body.setAttribute('data-bg', 'disabled');
} else if (bgParam === 'enabled') {
  document.body.setAttribute('data-bg', 'enabled');
}
// If no param, useConfig will set it later based on game config

// Force browser to recalculate layout - mimics what happens after rotation
const forceLayoutRecalculation = () => {
  // Method 1: Dispatch resize event (triggers media query re-evaluation)
  window.dispatchEvent(new Event('resize'));
  
  // Method 2: Force reflow by reading offsetHeight
  document.body.offsetHeight;
  
  // Method 3: Toggle a class to force style recalculation
  document.documentElement.classList.add('force-recalc');
  requestAnimationFrame(() => {
    document.documentElement.classList.remove('force-recalc');
  });
};

// Set viewport dimensions as CSS variables
const setViewportDimensions = () => {
  const doc = document.documentElement;
  doc.style.setProperty('--app-height', `${window.innerHeight}px`);
  doc.style.setProperty('--app-width', `${window.innerWidth}px`);
};

// Initialize immediately
setViewportDimensions();

// After DOM is ready, force layout recalculation
window.addEventListener('DOMContentLoaded', () => {
  setViewportDimensions();
  
  // Force layout recalculation at multiple intervals to catch Safari's delayed calculations
  if (isIPhone) {
    setTimeout(forceLayoutRecalculation, 0);
    setTimeout(forceLayoutRecalculation, 50);
    setTimeout(forceLayoutRecalculation, 100);
    setTimeout(forceLayoutRecalculation, 200);
    setTimeout(forceLayoutRecalculation, 500);
  }
});

// On resize
window.addEventListener('resize', setViewportDimensions);

// On orientation change
window.addEventListener('orientationchange', () => {
  setViewportDimensions();
  setTimeout(forceLayoutRecalculation, 100);
});

// Handle back/forward cache
window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    setViewportDimensions();
    forceLayoutRecalculation();
  }
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Router>
      <App />
    </Router>
  </StrictMode>
);
